import {axiosBaseQuery} from "@/helpers/axiosBaseQuery";
import api from "@/store/api/api";
import {createApi} from "@reduxjs/toolkit/query/react";

export const desksApi = createApi({
    reducerPath: 'desksApi',
    tagTypes: ['desks', 'desk-single', 'desk-transactions'],
    baseQuery: axiosBaseQuery({
        baseUrl: api.root,
    }),
    endpoints: build => ({
        getDesks: build.query({
            query: (data) => ({
                url: api.desks.getDesks(),
                method: 'GET',
                params: data
            }),
            providesTags: ['desks']
        }),
        getDeskById: build.query({
            query: ({id}) => ({
                url: api.desks.getDeskById(id),
                method: 'GET',
            }),
            providesTags: (result, error, arg) =>
                result
                    ? [{type: 'desk-single', id: result.id}]
                    : ['desk-single'],
        }),
        getDesksDashboard: build.query({
            query: (data) => ({
                url: api.desks.getDesksDashboard(),
                method: 'GET',
                params: data
            }),
            providesTags: ['desks']
        }),
        createDesk: build.mutation({
            query: (data) => ({
                url: api.desks.createDesk(),
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['desks']
        }),
        updateDesk: build.mutation({
            query: ({id, ...data}) => ({
                url: api.desks.updateDesk(id),
                method: 'PUT',
                body: data
            }),
            invalidatesTags: (result, error, arg) => [{type: 'desk-single', id: arg.id}, 'desks'],
        }),
        deleteDesk: build.mutation({
            query: ({id, location_id}) => {
                const query = {
                    url: api.desks.deleteDesk(id),
                    method: 'DELETE',
                };
                if (location_id != null) {
                    query.params = {location_id};
                }
                return query;
            },
            invalidatesTags: ['desks']
        }),
        getDeskTransactions: build.query({
            query: (data) => ({
                url: api.desks.getDeskTransactions(),
                method: 'GET',
                params: data
            }),
            serializeQueryArgs: ({endpointName, queryArgs}) => {
                return [endpointName, queryArgs?.location_id]
            },
            merge: (currentCache, newItems, {arg}) => {
                if (arg.skip === 0) {
                    return newItems;
                }
                currentCache.push(...newItems.filter((newItem) => !currentCache.some((item) => item.id === newItem.id)))
            },
            forceRefetch: ({currentArg, previousArg}) => {
                return currentArg !== previousArg;
            },
            providesTags: ['desk-transactions']
        }),
        createDeskTransaction: build.mutation({
            query: (data) => ({
                url: api.desks.createDeskTransaction(),
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['desk-transactions', 'desks']
        }),
        updateDeskTransaction: build.mutation({
            query: ({id, ...data}) => ({
                url: api.desks.updateDeskTransaction(id),
                method: 'PUT',
                body: data
            }),
            invalidatesTags: ['desk-transactions', 'desks']
        }),
        deleteDeskTransaction: build.mutation({
            query: ({id}) => ({
                url: api.desks.deleteDeskTransaction(id),
                method: 'DELETE',
            }),
            invalidatesTags: ['desk-transactions', 'desks']
        }),
        transferBetweenDesks: build.mutation({
            query: (data) => ({
                url: api.desks.transferBetweenDesks(),
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['desk-transactions', 'desks']
        }),
        getDeskTransactionStatistics: build.query({
            query: (data) => ({
                url: api.desks.getDeskTransactionStatistics(),
                method: 'GET',
                params: data
            }),
        }),
        getDeskTransactionById: build.query({
            query: ({id, ...data}) => ({
                url: api.desks.getDeskTransaction(id),
                method: 'GET',
                params: data
            }),
        }),
    })
});

export const {
    useGetDesksQuery,
    useGetDesksDashboardQuery,
    useDeleteDeskMutation,
    useLazyGetDeskTransactionByIdQuery,
} = desksApi
